import { Iso8601Date, Iso8601DateTime } from '@/lib/graphql/scalars';

export type DateInput = Date | Iso8601Date | Iso8601DateTime | string;

const MS_IN_DAY = 1000 * 60 * 60 * 24;
const MS_IN_WEEK = MS_IN_DAY * 7;

/** Given a date-like representation, return a date object */
export const getDateObject = (date: DateInput, naive = false): Date => {
  if (date instanceof Date) {
    return date;
  }
  if (naive) {
    return parseLocalDate(date);
  }
  return new Date(date);
};

/**
 * Parse a date string YYYY-MM-DD into a Date object in the local timezone.
 * By default, `new Date()` will parse the date as if it is UTC, resulting in
 * date shift.
 */
export const parseLocalDate = (str: string): Date => {
  // Split ISO string format into [year, month, day, hour, minute, second, ms], discarding timezone info
  let ds = str.split(/\D/).map((d) => parseInt(d, 10));
  ds[1] = ds[1] - 1; // adjust month to zero-index;
  ds = ds.slice(0, 6); // discard ms and timezone info if present
  // @ts-ignore TypeScript dislikes the use of spread with variable length outcome for function calls, but Date() handles it
  return new Date(...ds);
};

/**
 * Determine difference of years from two dates used in the app.
 *
 * @returns A positive integer if date is in the past
 */
export const numberOfYearsDiff = (from: Date, to: Date): number => {
  const yearDiff = to.getUTCFullYear() - from.getUTCFullYear();
  const monthDiff = to.getUTCMonth() - from.getUTCMonth();
  const dateDiff = to.getUTCDate() - from.getUTCDate();
  let result = yearDiff;

  if (monthDiff < 0) {
    result--;
  } else if (monthDiff === 0 && dateDiff < 0) {
    result--;
  }
  return result;
};

/**
 * Determine difference of weeks from two dates used in the app.
 *
 * @returns A positive integer if date is in the past
 */
export const numberOfWeeksDiff = (from: Date, to: Date): number => {
  return Math.ceil((to.getTime() - from.getTime()) / MS_IN_WEEK);
};

/**
 * Determine difference of days from two dates.
 *
 * @returns Diff in days as an integer value
 */
export const numberOfDaysDiff = (from: Date, to: Date): number => {
  return Math.ceil((from.getTime() - to.getTime()) / MS_IN_DAY);
};

/**
 * Adds x number of weeks to a date
 *
 * @returns A local date string, in format
 */
export const addWeeksToDate = (dateObj: Date, numberOfWeeks: number) => {
  // Ensure we don't mutate the original date;
  const newDate = new Date(dateObj);
  newDate.setDate(newDate.getDate() + numberOfWeeks * 7);
  return newDate;
};
